import 'lazysizes';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import get from 'lodash/fp/get';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router';
import { LANGUAGES } from 'config/types/prop';
import createGetTranslation from 'selectors/i18n';
import isNode from 'utils/environment';
import { getInfo } from 'selectors';
import getIs404 from 'selectors/getIs404';
import getPathMap from 'selectors/getPathMap';
import { pathTypeOrder } from 'config/types/path';
import { containerMap } from 'config/types/content/component';
import { getEdition } from 'selectors/getEdition';
import getLocale from 'selectors/getLocale';
import getMatch from 'selectors/getMatch';
import getIsInProductMagazine from 'selectors/getIsInProductMagazine';
import getPageTrackingData from 'selectors/getPageTrackingData';
import { options } from 'config/reactRedux';
import Header from 'components/header';
import Footer from 'components/footer';
import PageShownTracking from 'components/pageShownTracking';
import NotFound from 'components/notFound';
import MarketingTracker from 'components/marketingTracker';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  defaultLanguage,
  languageCodeEnglishFullNames,
  languageCodePostTypeWithEnglishMap,
} from 'config/types/language';
import { getFacebookLocale } from 'utils/metadata';
import { buildShortcodeUrl, isTrackingDisabled } from 'utils/url';
import { trackPageEvent } from 'utils/tracking';
import isInMobileApp from 'utils/isInMobileApp';
import MastheadLanguagePicker from 'components/languagePicker/masthead';
import ShortcodePixel from 'components/tracking/shortcodePixel';
import { fetchUser as fetchUserAction } from 'actions';
import ContentEmbedProvider from 'components/contentEmbedProvider';
import getSessionStartedStatus from 'utils/getSessionStartedStatus';

import styles from './app.css';

const App = ({
  facebookLocale,
  is404,
  isContentLoading,
  locale,
  location,
  magazineEditionLanguage,
  pathMap,
  title,
  getTranslation,
  fetchUser,
  isInProductMagazine,
  pageTrackingData,
}) => {
  const shortcode = queryString.parse(location.search).bsc;
  const languageEnglishName = (
    languageCodeEnglishFullNames[locale]
  );
  const rssEdition = (
    languageCodePostTypeWithEnglishMap[locale]
  );
  const [enablePageShownTracking, setEnablePageShownTracking] = useState(false);
  const hasSessionStarted = useRef(getSessionStartedStatus());
  const { data, isLoading } = pageTrackingData;
  const lastValidTrackingDataRef = useRef();

  useEffect(() => {
    if (!is404) {
      lastValidTrackingDataRef.current = data;
    }

    const unloadingEventType = isInMobileApp ? 'beforeunload' : 'pagehide';
    const removeUserSession = () => {
      if (lastValidTrackingDataRef.current) {
        trackPageEvent({ name: 'magazine_page:session_ended', version: 1, ...lastValidTrackingDataRef.current });
      }
      window.sessionStorage.removeItem('session_started');
    };

    window.addEventListener(unloadingEventType, removeUserSession);

    return () => {
      window.removeEventListener(unloadingEventType, removeUserSession);
    };
  }, [data, is404]);

  useEffect(() => {
    if (!hasSessionStarted.current && !isLoading && !is404) {
      trackPageEvent({ name: 'magazine_page:session_started', version: 1, ...data });
      window.sessionStorage.setItem('session_started', Date.now());
      hasSessionStarted.current = true;
    }
  }, [isLoading, is404]);

  useEffect(() => {
    fetchUser();
    setEnablePageShownTracking(true);
  }, []);

  return (
    <div className={classNames(styles.wrapper, { [styles.wrapperInProduct]: isInProductMagazine })}>
      <MarketingTracker
        isContentLoading={isContentLoading}
        location={location}
        magazineEditionLanguage={magazineEditionLanguage}
      />
      {!isNode() && shortcode && <ShortcodePixel url={buildShortcodeUrl(shortcode)} />}
      <Helmet>
        <title>Babbel Magazine</title>
        <meta name="babbel:locale" content={magazineEditionLanguage} />
        <meta name="babbel:facebook:locale" content={facebookLocale} />
        <meta property="og:site_name" content={getTranslation('meta-title', 'Babbel Magazine')} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`Babbel Magazine - ${languageEnglishName} Edition`}
          href={`https://cms.babbel.news/feed/?post_type=${rssEdition}`}
        />
      </Helmet>
      {enablePageShownTracking && !is404 && <PageShownTracking />}
      <Header />
      {!isInProductMagazine && <MastheadLanguagePicker />}
      <main className={styles.mainWrapper}>
        <div className={styles.mainWrapperContent}>
          <ContentEmbedProvider>
            <Switch>
              {is404 && <Route component={NotFound} />}
              {!is404 && pathTypeOrder.map((pathType) => (
                pathMap[pathType] && <Route
                  exact
                  path={pathMap[pathType]}
                  component={containerMap[pathType]}
                  key={pathType}
                />
              ))}
            </Switch>
          </ContentEmbedProvider>
        </div>
      </main>
      {!isInProductMagazine && <Footer />}
    </div>
  );
};

App.propTypes = {
  facebookLocale: PropTypes.string.isRequired,
  is404: PropTypes.bool.isRequired,
  isContentLoading: PropTypes.bool.isRequired,
  locale: LANGUAGES.isRequired,
  location: PropTypes.object.isRequired,
  magazineEditionLanguage: PropTypes.string.isRequired,
  pathMap: PropTypes.object.isRequired,
  title: PropTypes.string,
  getTranslation: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  isInProductMagazine: PropTypes.bool.isRequired,
  pageTrackingData: PropTypes.object.isRequired,
};

App.defaultProps = {
  magazineEditionLanguage: defaultLanguage,
  title: 'Babbel Magazine',
};

const mapStateToProps = (state) => {
  const isContentLoading = get('active.loading.content', state);
  const pathMatch = getMatch(state);
  const locale = getLocale(state);
  const currentEdition = getEdition(state);
  const getTranslation = createGetTranslation(state);

  return {
    pathMap: getPathMap(state),
    title: getInfo(state, 'title.value'),
    is404: getIs404(state),
    pathMatch,
    isContentLoading,
    locale,
    magazineEditionLanguage: currentEdition,
    facebookLocale: getFacebookLocale(locale),
    getTranslation,
    isInProductMagazine: getIsInProductMagazine(state),
    pageTrackingData: getPageTrackingData(state),
  };
};

const mapDispatchToProps = {
  fetchUser: fetchUserAction,
};

const withRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  undefined,
  options,
);

const wrappedWithStyles = withStyles(styles);

// withRouter HoC fixes Redux and router state becoming out of sync.
// See: https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/redux.md
export default withRouter(wrappedWithStyles(withRedux(App)));
export {
  App,
  mapStateToProps,
};
